/**
 * Opening markdown files dropped onto the window.
 *
 * Dropped anywhere — the document, the sidebar, the title bar — a markdown
 * file opens in a tab, as File > Open would. Other files, images among them,
 * are left to the editor, which inserts dropped images into the document.
 *
 * Web: a browser gives no file's place on disk. Where it gives a handle (File
 * System Access), the file opens from it and saves back where it came from;
 * elsewhere it opens as a copy, which saves by downloading. A dropped folder
 * opens as the workspace, as File > Open Folder would. The handles can only be
 * asked for during the drop event, so they are gathered there.
 */
import { OPENABLE } from '../../shared/openable'
import { adoptFile, openPath, requestEditorFocus } from './stores/documents'
import { reportError } from './utils/report'

const wanted = (name: string): boolean => OPENABLE.test(name)

async function open(reading: ReturnType<typeof window.api.web.readDrop>): Promise<void> {
  let dropped
  try {
    dropped = await reading
  } catch (err) {
    reportError('read dropped files', err)
    return
  }
  const copies: File[] = []
  for (const d of dropped) {
    try {
      if (d.kind === 'path') await openPath(d.path)
      else if (d.kind === 'file') copies.push(d.file)
      else await window.api.web.openDroppedFolder(d)
    } catch (err) {
      reportError('open dropped item', err)
    }
  }
  if (copies.length === 0) return
  try {
    for (const f of await window.api.web.openCopies(copies)) requestEditorFocus(adoptFile(f).id)
  } catch (err) {
    reportError('open dropped files', err)
  }
}

/** Starts accepting dropped markdown files and folders; returns a function that stops. */
export function installFileDrop(): () => void {
  // Without this the browser refuses a drop anywhere but the editor.
  const onDragOver = (event: DragEvent): void => {
    if ([...(event.dataTransfer?.types ?? [])].includes('Files')) event.preventDefault()
  }
  const onDrop = (event: DragEvent): void => {
    const dt = event.dataTransfer
    if (!dt || !window.api.web.dropHasOpenable(dt, wanted)) return
    // Claimed before the editor sees it, or it would try to insert the file.
    event.preventDefault()
    event.stopPropagation()
    void open(window.api.web.readDrop(dt, wanted))
  }
  window.addEventListener('dragover', onDragOver, true)
  window.addEventListener('drop', onDrop, true)
  return () => {
    window.removeEventListener('dragover', onDragOver, true)
    window.removeEventListener('drop', onDrop, true)
  }
}
